import React, { useState, useEffect } from "react";
import {
  Card,
  FormControlLabel,
  Grid,
  Icon,
  Radio,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Select,
  FormControl,
  Autocomplete,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  selectMenuProps,
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { ArrowDropDown } from "@mui/icons-material";
import {
  REGEX,
  DEFAULT_RECORDS_PER_PAGE,
  STATUS,
} from "components/common/constant";
import {
  addUser,
  getUserList,
  getUserById,
  updateUserById,
  deleteUserById,
} from "actions/user";
import { useDispatch, useSelector } from "react-redux";
import { userList } from "constants/user";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { getDepartmentList } from "actions/department";
import { departmentList } from "constants/department";
import { getRoleListing } from "actions/role";
import { roleList } from "constants/role";
import { hasAccess } from "components/common/constant";

const initialFields = {
  id: null,
  name: {
    value: "",
    error: "",
    isChange: false,
  },
  email: {
    value: "",
    error: "",
    isChange: false,
  },
  role: {
    value: "",
    error: "",
    isChange: false,
  },
  department: {
    value: "", //[]
    error: "",
    isChange: false,
  },
  createdBy: {
    value: "Admin",
    error: "",
    isChange: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChange: false,
  },
};

function UserListing() {
  const dispatch = useDispatch();
  const userListData = useSelector((state) => state.user.userList);
  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  const roleListData = useSelector((state) => state.role.roleList);

  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("INTERNAL_USERS", "CREATE")?.includes(
    currentRole,
  );
  const canUpdate = hasAccess("INTERNAL_USERS", "UPDATE")?.includes(
    currentRole,
  );
  const canDelete = hasAccess("INTERNAL_USERS", "DELETE")?.includes(
    currentRole,
  );

  const canShowActions = canUpdate || canDelete;

  const { notify } = useNotify();
  const [pageNum, setPageNum] = useState(1);
  const [panelMode, setPanelMode] = useState(null); //add /edit
  const [userFormData, setUserFormData] = useState(initialFields);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    role: null,
    department: null,
    status: null,
    filterParams: [],
  });

  useEffect(() => {
    getDepartmentList(dispatch);
    getRoleListing(dispatch);
    return () => {
      dispatch({ type: departmentList, payload: {} });
      dispatch({ type: roleList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    getUserList(dispatch, pageNum, filters.filterParams);
    return () => {
      dispatch({ type: userList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (userListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: userListData?.data?.User?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (userListData?.code || userListData?.message) {
      setListState({
        isLoading: false,
        error:
          userListData?.message ||
          "Failed to load user list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [userListData]);

  const formattedListData = userListData?.data?.User?.filter(
    (item) => item?.role?.name !== "Technician",
  )?.map((item, index) => ({
    id: item?.ID,
    name: item?.name || "-",
    email: item?.email || "-",
    status: item?.status,
    role: item?.role?.name || "-",
    department:
      item?.departments?.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: "18px" }}>
          {item.departments.map((d, i) => (
            <li key={i}>{d.name}</li>
          ))}
        </ul>
      ) : (
        "-"
      ),
    createdBy: item?.createdBy || "-",
    srNo:
      (pageNum - 1) *
        (userListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
  }));

  const handleChange = (field, value) => {
    setUserFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChange: true,
      },
    }));
  };

  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([key, field]) => field?.isChange && key !== "id")
        .map(([key, field]) => {
          if (key === "role") return ["roleId", field.value?.id];
          if (key === "department")
            return ["deptIds", field.value.map((d) => d.id)];
          return [key, field.value];
        }),
    );

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const isEdit = panelMode === "edit";
    let bodyData = {
      name: userFormData.name.value,
      status: userFormData.status.value,
      email: userFormData.email.value,
      roleId: userFormData.role.value?.id,
      deptIDs: userFormData.department.value.map((d) => d.id),
    };
    if (isEdit) {
      const editedData = getEditedBodyData(userFormData);
      if (Object.keys(editedData).length === 0) {
        notify("No changes detected", "info");
        return;
      }
      bodyData = editedData;
    }
    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const res = isEdit
        ? await updateUserById(userFormData.id, bodyData)
        : await addUser(bodyData);
      if (res?.code === 200) {
        notify(
          res?.message || `User ${isEdit ? "updated" : "added"} successfully`,
          "success",
        );
        closeModal();
        // pageNum === 1
           getUserList(dispatch, pageNum, filters.filterParams)
          // : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to update user";
        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.message || `Failed to ${isEdit ? "update" : "add"} user`,
        "error",
      );
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setUserFormData({
      ...initialFields,
    });
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        return !trimmedValue ? "Please enter full name" : "";

      case "email":
        if (!trimmedValue) return "Please enter email";
        if (!REGEX.EMAIL_REGEX.test(trimmedValue))
          return "Please enter a valid email address";
        return "";

      case "role":
        return !trimmedValue ? "Please select role" : "";

      case "department":
        return !trimmedValue || trimmedValue.length === 0
          ? "Please select department"
          : "";

      // case "createdBy":
      //   return !trimmedValue ? "Please enter created by" : "";

      default:
        return "";
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setUserFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...userFormData };

    Object.keys(newData).forEach((key) => {
      if (key === "id") return;

      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setUserFormData(newData);
    return Object.values(newData)
      .filter((item, i) => Object.keys(newData)[i] !== "id")
      .every((item) => item.error === "");
  };

  const openAddModal = () => {
    // e.stopPropagation();
    setPanelMode("add");
    setUserFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const res = await getUserById(row.id);

      if (res?.code === 200) {
        const user = res.data;
        const selectedRole = roleOptions.find((r) => r.id === user?.role?.ID);
        const selectedDepartments =
          user?.departments
            ?.map((dept) => departmentOptions.find((d) => d.id === dept.ID))
            .filter(Boolean) || [];

        setUserFormData({
          id: user?.ID,
          name: {
            value: user?.name || "",
            error: "",
            isChange: false,
          },
          email: {
            value: user?.email || "",
            error: "",
            isChange: false,
          },
          role: {
            value: selectedRole || null,
            error: "",
            isChange: false,
          },
          department: {
            value: selectedDepartments || [],
            error: "",
            isChange: false,
          },
          status: {
            value: user?.status || "ACTIVE",
            error: "",
            isChange: false,
          },
        });
      }
    } catch (error) {
      notify(
        error?.message || "Something went wrong while fetching user details",
        "error",
      );
      closeModal();
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleDelete = (row) => {
    showAlert({
      title: `Are you sure you want to delete this user?`,
      message: `User "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));
          const res = await deleteUserById(row.id);
          if (res?.code === 200) {
            notify(res?.message || "User deleted successfully", "success");
            pageNum === 1
              ? getUserList(dispatch, pageNum, filters.filterParams)
              : setPageNum(1);
          } else {
            notify(res?.message || "Failed to delete user", "error");
          }
        } catch (error) {
          notify(
            error?.message || "Something went wrong while deleting user",
            "error",
          );
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },
      onCancel: () => {},
    });
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    return (
      <MDBadge
        badgeContent={status}
        color={status === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const columns = [
    {
      Header: "Sr No.",
      accessor: "srNo",
      align: "left",
      width: "90px",
    },
    {
      Header: "Name",
      accessor: "name",
      align: "left",
      width: "150px",
      Cell: (cell) => wrapCell(cell.value, "150px"),
    },
    {
      Header: "Email",
      accessor: "email",
      align: "left",
      width: "250px",
      Cell: (cell) => wrapCell(cell.value, "230px"),
    },
    {
      Header: "Role",
      accessor: "role",
      align: "left",
      width: "150px",
      Cell: (cell) => wrapCell(cell.value, "150px"),
    },
    {
      Header: "Department",
      accessor: "department",
      align: "left",
      width: "150px",
      Cell: (cell) => wrapCell(cell.value, "120px"),
    },
    {
      Header: "Created By",
      accessor: "createdBy",
      align: "left",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value, "120px"),
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
      width: "120px",
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            width: "100px",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  const roleOptions =
    roleListData?.data?.Role?.filter((r) => r.name !== "Technician")?.map(
      (r) => ({
        id: r.ID,
        name: r.name,
      }),
    ) || [];

  const departmentOptions =
    departmentListData?.data?.Department?.map((d) => ({
      id: d.ID,
      name: d.name,
      status: d.status,
    })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.name) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name],
      });
    }

    if (filters.email) {
      filterParams.push({
        field: "Email",
        condition: "eq",
        filterValues: [filters.email],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "DeptID",
        condition: "eq",
        filterValues: [String(filters.department)],
      });
    }

    if (filters.role) {
      filterParams.push({
        field: "RoleID",
        condition: "eq",
        filterValues: [String(filters.role)],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newfilterParams)
    ) {
      setUserFormData((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilters((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setFilters({
      name: "",
      email: "",
      role: null,
      department: null,
      status: null,
      filterParams: [],
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Internal Users
                </MDTypography>

                {canCreate && (
                  <MDButton
                    type="button"
                    variant="contained"
                    color="deepBlue"
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      textTransform: "none",
                    }}
                    onClick={openAddModal}
                  >
                    Add Internal User
                  </MDButton>
                )}
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      placeholder="Search by name"
                      size="small"
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      placeholder="Search by email"
                      size="small"
                      value={filters.email}
                      onChange={(e) =>
                        setFilters({ ...filters, email: e.target.value })
                      }
                    />
                  </Grid>

                  {/* Role Filter */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={roleOptions || []}
                      value={
                        roleOptions.find((r) => r.id === filters.role) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          role: newValue?.id || null,
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select role" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentOptions.filter(
                        (d) => d.status === "ACTIVE",
                      )}
                      value={
                        departmentOptions.find(
                          (d) => d.id === filters.department,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.id || null,
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select department"
                        />
                      )}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        IconComponent={ArrowDropDown}
                        MenuProps={selectMenuProps}
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                Select Status
                              </span>
                            );

                          return renderStatusBadge(selected);
                        }}
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Search Button */}
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox display="flex" gap={1} flexWrap="wrap" width="100%">
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox
                px={3}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {userListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography color="error" variant="body2">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="20vh"
                  >
                    <MDTypography color="text" variant="body2">
                      No users found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={formattedListData || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (userListData?.data?.totalRecords || 0) /
                        (userListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              {panelMode === "edit"
                ? "Edit Internal User"
                : "Add Internal User"}
            </MDTypography>

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Name</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="name"
                    placeholder="Enter name"
                    value={userFormData.name.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!userFormData.name.error}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />
                  {userFormData.name.error && (
                    <MDTypography variant="caption" color="error">
                      {userFormData.name.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Email</RequiredLabel>
                  <MDInput
                    type="text"
                    placeholder="Enter Email"
                    name="email"
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    value={userFormData.email.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    variant="outlined"
                    error={!!userFormData.email.error}
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                  />
                  {userFormData.email.error && (
                    <MDTypography variant="caption" color="error">
                      {userFormData.email.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Role</RequiredLabel>

                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disablePortal
                    options={roleOptions}
                    getOptionLabel={(option) => option.name || ""}
                    value={userFormData.role.value || null}
                    onChange={(event, newValue) =>
                      handleChange("role", newValue)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select role"
                        error={!!userFormData.role.error}
                        helperText={userFormData.role.error}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Department</RequiredLabel>

                  <Autocomplete
                    {...autoCompleteProps}
                    multiple
                    size="small"
                    disablePortal
                    options={departmentOptions.filter(
                      (d) => d.status === "ACTIVE",
                    )}
                    getOptionLabel={(option) => option.name || ""}
                    value={userFormData.department.value || []}
                    onChange={(event, newValue) =>
                      handleChange("department", newValue)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Department"
                        error={!!userFormData.department.error}
                        helperText={userFormData.department.error}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={userFormData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        </>
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default UserListing;
